import React, {
  useCallback, useRef, useState
} from 'react';

import { QuestionIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import isFunction from 'lodash/isFunction';

import { getThemeColor } from '../../../utils/theme-utils';
import { isDNSCompliantString } from '../../../utils/text_utils';

import Button, { BUTTON_VARIANT } from '../../common/Button/Button';
import BlankSlate from '../../common/BlankSlate/BlankSlate';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import SearchInput from '../../common/TextInputs/SearchInput/SearchInput';
import TextInputWithError from '../../common/TextInputs/TextInputWithError/TextInputWithError';
import WorkspaceIcon from '../../common/icons/WorkspaceIcon';

import WorkspaceList from '../WorkpaceList/WorkspaceList';

import useUserContext from '../../contexts/UserData/UserData';
import useWorkspaceContext from '../WorkspaceContext/WorkspaceContext';

const QuestionMarkGraphic = () => (
  <StyledOcticon
    className="text-brand"
    icon={QuestionIcon}
    size={64}
  />
)

export const NoWorkspacesSlate = () => (
  <BlankSlate
    className="p-4"
    mainText="Looks like there's no workspaces for you."
    subText="Get started by creating one below!"
    Graphic={QuestionMarkGraphic}
  />
)

const WorkspaceCreationPrompt = ({ onWorkspaceCreation }) => {
  const inputRef = useRef(null);
  const [messageText, setMessageText] = useState('')
  const [hasError, setHasError] = useState(false);

  const onEntryValidation = value => value?.length && value?.trim()?.length && isDNSCompliantString(value);

  const onSubmit = (value) => {
    if (!hasError) {
      onWorkspaceCreation(value)
        .then(() => setMessageText(''))
        .catch(() => setHasError(true))
    }
  };

  const onSubmitClick = () => {
    if (onEntryValidation(messageText)) {
      onSubmit(messageText);
    }
  };

  const onChange = (value) => {
    setHasError(false);
    setMessageText(value);
  }

  const onValueError = (entry) => {
    setHasError(true);
    return !entry?.length ? 'Please enter a name for your new workspace' : 'Workspace name must be unique and DNS compliant.';
  }

  return (
    <div className="p-4 mb-2 border rounded flex items-center">
      <TextInputWithError
        type="text"
        ref={inputRef}
        className="ml-2 flex-grow px-2 py-1 w-full"
        placeholder="Workspace name"
        value={messageText}
        onChange={onChange}
        onSubmit={onSubmit}
        onEntryValidation={onEntryValidation}
        onValueError={onValueError}
        error={hasError ? 'error' : ''}
      />
      <Button disabled={hasError || !messageText?.length} className="ml-4" onClick={onSubmitClick}>Create Workspace</Button>
    </div>
  )
}

const WorkspaceSearchRow = ({ searchQuery, setSearchQuery }) => (
  <div className="p-1 ml-1 w-full flex flex-row items-center justify-start">
    <SearchInput
      className="relative flex-grow border border-border-dark rounded py-2 pl-10 mr-2 bg-white"
      ariaLabel="Search by workspace name"
      placeholder="Search by workspace name"
      value={searchQuery}
      onChange={event => setSearchQuery(event.target.value)}
    />
  </div>
)

export const WorkspaceIndex = ({
  currentUser,
  workspaces,
  handleWorkspaceCreation,
  handleJoinWorkspace,
  onCloseMenu,
  isFirstTimeUser = false,
  disableWorkspaceCreation = false,
}) => {
  const [selectedWorkspaceId, setSelectedWorkspace] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const onJoinWorkspace = useCallback(() => {
    if (isFunction(handleJoinWorkspace) && currentUser?.id && selectedWorkspaceId) {
      handleJoinWorkspace(selectedWorkspaceId, currentUser?.id);

      if (isFunction(onCloseMenu)) {
        onCloseMenu();
      }
    }
  }, [currentUser.id, handleJoinWorkspace, onCloseMenu, selectedWorkspaceId]);

  const onWorkspaceCreated = useCallback(workspaceName => handleWorkspaceCreation(workspaceName)
    .then(() => {
      if (isFunction(onCloseMenu)) {
        onCloseMenu();
      }
    }), [handleWorkspaceCreation, onCloseMenu]);
  return (
    <CardView>
      <div className="mb-2 flex items-center">
        <Icon fill={getThemeColor('brand')} icon={WorkspaceIcon} size={24} useTailwindSizing />
        <CardHeader className="ml-2" bold>{isFirstTimeUser ? 'Welcome to Wallaroo!' : 'Workspaces'}</CardHeader>
      </div>
      {
        isFirstTimeUser && (
          <Label className="mb-2" size="sm">Since it&apos;s your first time here, please select a workspace to view or create your own.</Label>
        )
      }
      <WorkspaceSearchRow searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      {
        workspaces?.length ? (
          <WorkspaceList
            className="mb-2 p-2"
            selectedWorkspaceId={selectedWorkspaceId}
            workspaces={workspaces.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()))}
            onWorkspaceClick={setSelectedWorkspace}
          />
        ) : (
          <NoWorkspacesSlate />
        )
      }
      {
        !disableWorkspaceCreation ? (
          <WorkspaceCreationPrompt onWorkspaceCreation={onWorkspaceCreated} />
        ) : null
      }
      <Button
        className="w-full"
        variant={BUTTON_VARIANT.PRIMARY}
        disabled={!selectedWorkspaceId}
        onClick={onJoinWorkspace}
      >
        View Workspace
      </Button>
    </CardView>
  )
}

const ContextualWorkspaceIndex = (props) => {
  const { userData } = useUserContext();
  const { workspaces, addWorkspace, setCurrentWorkspaceId } = useWorkspaceContext();

  const handleJoinWorkspace = useCallback((selectedWorkspaceId) => {
    if (setCurrentWorkspaceId) {
      setCurrentWorkspaceId(selectedWorkspaceId);
    }
  }, [setCurrentWorkspaceId]);

  const handleAddWorkspace = useCallback((workspaceName) => {
    if (addWorkspace) {
      return addWorkspace(workspaceName)
        .then((data) => { if (setCurrentWorkspaceId) setCurrentWorkspaceId(data.data.insert_workspace_one.id) });
    }
  }, [addWorkspace, setCurrentWorkspaceId])

  return (
    <WorkspaceIndex
      currentUser={userData}
      workspaces={workspaces}
      handleWorkspaceCreation={handleAddWorkspace}
      handleJoinWorkspace={handleJoinWorkspace}
      {...props}
    />
  )
}

export default ContextualWorkspaceIndex;
